/**
 * Application configuration — stage-aware settings.
 *
 * Gateway URL is resolved from the current hostname:
 *   localhost:*              → local dev (http://localhost:8081)
 *   beta.rocky.ai.aws.dev   → beta
 *   gamma.rocky.ai.aws.dev  → gamma
 *   rocky.ai.aws.dev        → prod
 *
 * Federate OIDC config reuses AgentHubPortal Service Profile.
 */

// ── Federate constants ──────────────────────────────────────────────────────

const FEDERATE_AUTH_PATH = '/api/oauth2/v1/authorize';
const FEDERATE_ID_TOKEN_KEY = 'rocky_federate_id_token';
const FEDERATE_ACCESS_TOKEN_KEY = 'rocky_federate_access_token';

// ── Types ───────────────────────────────────────────────────────────────────

export interface StageConfig {
  stage: string;
  gatewayUrl: string;
  /** Internal API base URL (auth callback, etc.) */
  internalUrl: string;
  federateClientId: string;
  identityProvider: string;
  /** AWS region for Cognito + API Gateway */
  region: string;
  /** Cognito Identity Pool ID for SigV4 credential exchange */
  identityPoolId: string;
  /** CMHKAgentPlatformService REST API endpoint (Skill Marketplace) */
  skillApiEndpoint: string;
  /** CMHKScheduleTaskService REST API endpoint */
  scheduleTaskApiEndpoint: string;
  /** IVTHub REST API endpoint (Solution Map / Assets / Motions). 'TBD' until backend onboards the stage. */
  ivtApiEndpoint: string;
  /** CMHKAgentPlatform Evaluation Service REST API endpoint */
  evalApiEndpoint: string;
  /**
   * Credit Program Advisor (SalesRecommendationHub) REST API endpoint, aws partition us-east-1.
   * Signed for us-east-1 (CPA_REGION in cpa/client/CpaClient), not `region`.
   */
  cpaApiEndpoint: string;
  /** Event Hub module config (AWSECEventHub). See EventHubStageConfig. */
  eventHub: EventHubStageConfig;
}

/**
 * Per-stage config for the Event Hub module (AWSECEventHub), namespaced so the
 * away-team's fields don't flatten into the shared StageConfig.
 *
 * NOTE on regions: they are NOT here on purpose. The Cognito pool always lives
 * in BJS (cn-north-1, the only aws-cn region with Cognito) and the EventHub API
 * always in ZHY (cn-northwest-1). Those are fixed platform facts, not per-stage
 * settings, so they live as constants in EventHubClient.
 */
export interface EventHubStageConfig {
  /**
   * AWSECEventHub API endpoint. Same-stage as the Rocky origin — the service
   * allowlists only this stage's Rocky origin for CORS; auth is SigV4 (AWS_IAM)
   * signed with creds from the Cognito pool below.
   */
  apiEndpoint: string;
  /**
   * AWSECEventHub Cognito Identity Pool (aws-cn / BJS), distinct from the app's
   * us-west-2 `identityPoolId`. Federate id_token → this pool → temp AWS creds →
   * SigV4 → EventHub API. 'TBD' until a stage's pool is provisioned.
   */
  identityPoolId: string;
  /**
   * OIDC login-provider key the pool trusts (Logins map key). Must match the
   * issuer host of the app's id_token — the pool trusts the app's prod Federate
   * identity (idp.federate.amazon.com / AgentHubPortal) on every stage.
   */
  identityProvider: string;
  /**
   * Octopus Flow Management Service (AWSOctopusFlowManagementService) REST API
   * endpoint (aws-cn / ZHY, execute-api). Used to mint QuickSight embed URLs
   * (GenerateEmbedUrlForRegisteredUser). Signed SigV4 with the SAME EventHub
   * Cognito creds as {@link apiEndpoint}, but with signing name `execute-api`
   * (the service model's own sigv4 name is not execute-api, so the signer is
   * overridden — see FlowManagementClient). 'TBD' until a stage is onboarded.
   */
  flowMgmtApiEndpoint: string;
}

// ── Stage map ───────────────────────────────────────────────────────────────

const STAGE_MAP: Record<string, StageConfig> = {
  'localhost': {
    stage: 'dev',
    gatewayUrl: 'https://beta.agenthub.gcr.aws.dev', // 'http://localhost:8082' for local Gateway testing
    internalUrl: 'https://internal.beta.agenthub.gcr.aws.dev',
    federateClientId: 'AgentHubPortal', // TODO: revert to 'AgentHubPortalPreProd' after testing
    identityProvider: 'idp.federate.amazon.com', // TODO: revert to 'idp-integ.federate.amazon.com' after testing
    region: 'us-west-2',
    identityPoolId: 'us-west-2:9ce784a8-48bd-4cb5-b462-bc91dc0aaaa8', // agentcore-backend-beta
    skillApiEndpoint: 'https://g718ybkkt7.execute-api.us-west-2.amazonaws.com/beta',
    scheduleTaskApiEndpoint: 'https://9jm3a8kc0h.execute-api.us-west-2.amazonaws.com/beta',
    // beta IVTHub endpoint — the localhost dev identity is the beta Cognito pool
    // (agentcore-backend-beta), so it must hit the beta IVTHub API. The alpha
    // endpoint rejects it at Layer 1 (AWS_IAM resource policy allowlists only
    // agentcore-backend-alpha). Matches the beta stage entry below.
    // The default execute-api hostname is switched off on the API
    // (IVTHubCDK serviceStack.ts `disableExecuteApiEndpoint: true`, added by
    // 73c8e52 to close a pentest finding), so every request to it returns a bare
    // 403 Forbidden before reaching the API. The custom domain is the only entry
    // point — same value the beta stage below already uses. Local dev keeps the
    // beta Cognito pool, so it must keep the beta IVTHub API.
    ivtApiEndpoint: 'https://beta.ivt.cmhk.aws.dev',
    evalApiEndpoint: "https://vz0gbr2crc.execute-api.us-west-2.amazonaws.com/beta",
    cpaApiEndpoint: 'https://uly97jnbr1.execute-api.us-east-1.amazonaws.com/beta',
    // Local dev origin (https://dev.rocky.ai.aws.dev:3000) is allowlisted on
    // the beta AWSECEventHub API for CORS, so point localhost at beta.
    eventHub: {
      apiEndpoint: 'https://beta.hub.event.marketing.aws.a2z.org.cn',
      identityPoolId: 'cn-north-1:30dfa66e-9eff-4517-a010-6b9ba0da4294',
      identityProvider: 'idp.federate.amazon.com',
      flowMgmtApiEndpoint: 'https://9p5eua3vue.execute-api.cn-northwest-1.amazonaws.com.cn/beta',
    },
  },
  'beta.rocky.ai.aws.dev': {
    stage: 'beta',
    gatewayUrl: 'https://beta.agenthub.gcr.aws.dev',
    internalUrl: 'https://internal.beta.agenthub.gcr.aws.dev',
    federateClientId: 'AgentHubPortal', // TODO: revert to 'AgentHubPortalPreProd' after testing
    identityProvider: 'idp.federate.amazon.com', // TODO: revert to 'idp-integ.federate.amazon.com' after testing
    region: 'us-west-2',
    identityPoolId: 'us-west-2:9ce784a8-48bd-4cb5-b462-bc91dc0aaaa8', // agentcore-backend-beta
    skillApiEndpoint: 'https://g718ybkkt7.execute-api.us-west-2.amazonaws.com/beta',
    scheduleTaskApiEndpoint: 'https://9jm3a8kc0h.execute-api.us-west-2.amazonaws.com/beta',
    ivtApiEndpoint: 'https://beta.ivt.cmhk.aws.dev',
    evalApiEndpoint: "https://vz0gbr2crc.execute-api.us-west-2.amazonaws.com/beta",
    cpaApiEndpoint: 'https://uly97jnbr1.execute-api.us-east-1.amazonaws.com/beta',
    eventHub: {
      apiEndpoint: 'https://beta.hub.event.marketing.aws.a2z.org.cn',
      identityPoolId: 'cn-north-1:30dfa66e-9eff-4517-a010-6b9ba0da4294',
      identityProvider: 'idp.federate.amazon.com',
      flowMgmtApiEndpoint: 'https://9p5eua3vue.execute-api.cn-northwest-1.amazonaws.com.cn/beta',
    },
  },
  'gamma.rocky.ai.aws.dev': {
    stage: 'gamma',
    gatewayUrl: 'https://gamma.agenthub.gcr.aws.dev',
    internalUrl: 'https://internal.gamma.agenthub.gcr.aws.dev',
    federateClientId: 'AgentHubPortal', // TODO: revert to 'AgentHubPortalPreProd' after testing
    identityProvider: 'idp.federate.amazon.com', // TODO: revert to 'idp-integ.federate.amazon.com' after testing
    region: 'us-west-2',
    identityPoolId: 'us-west-2:1083cef1-f097-4242-9c44-240f4e14a6bc', // agentcore-backend-gamma
    skillApiEndpoint: 'https://1pufb96i05.execute-api.us-west-2.amazonaws.com/gamma',
    scheduleTaskApiEndpoint: 'https://xh4o2az6q2.execute-api.us-west-2.amazonaws.com/gamma',
    ivtApiEndpoint: 'https://gamma.ivt.cmhk.aws.dev',
    evalApiEndpoint: "https://ri6z1spdmk.execute-api.us-west-2.amazonaws.com/gamma",
    cpaApiEndpoint: 'https://gpdunkczok.execute-api.us-east-1.amazonaws.com/gamma',
    eventHub: {
      apiEndpoint: 'https://gamma.hub.event.marketing.aws.a2z.org.cn',
      identityPoolId: 'cn-north-1:28d40f4f-6e8c-4569-abd1-84ab478617b2',
      identityProvider: 'idp.federate.amazon.com',
      flowMgmtApiEndpoint: 'https://76vuhg61g2.execute-api.cn-northwest-1.amazonaws.com.cn/gamma',
    },
  },
  'rocky.ai.aws.dev': {
    stage: 'prod',
    gatewayUrl: 'https://prod.agenthub.gcr.aws.dev',
    internalUrl: 'https://internal.prod.agenthub.gcr.aws.dev',
    federateClientId: 'AgentHubPortal',
    identityProvider: 'idp.federate.amazon.com',
    region: 'us-west-2',
    identityPoolId: 'us-west-2:2dcf139b-439b-4aa7-a5e1-8074c6f84744', // agentcore-backend-prod
    skillApiEndpoint: 'https://ekthiv45xe.execute-api.us-west-2.amazonaws.com/prod',
    scheduleTaskApiEndpoint: 'https://w3cbnjpoqd.execute-api.us-west-2.amazonaws.com/prod',
    ivtApiEndpoint: 'https://prod.ivt.cmhk.aws.dev',
    evalApiEndpoint: "https://s5rv3jx9hb.execute-api.us-west-2.amazonaws.com/prod",
    cpaApiEndpoint: 'https://9o5igk5vf0.execute-api.us-east-1.amazonaws.com/prod',
    eventHub: {
      apiEndpoint: 'https://prod.hub.event.marketing.aws.a2z.org.cn',
      identityPoolId: 'cn-north-1:6fe06280-e406-42ce-8436-8394d8bef0b7',
      identityProvider: 'idp.federate.amazon.com',
      flowMgmtApiEndpoint: 'https://x7ob2es411.execute-api.cn-northwest-1.amazonaws.com.cn/prod',
    },
  },
};

// ── Resolve ─────────────────────────────────────────────────────────────────

function resolveConfig(): StageConfig {
  const hostname = window.location.hostname;
  if (STAGE_MAP[hostname]) return STAGE_MAP[hostname];
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === 'dev.rocky.ai.aws.dev') return STAGE_MAP['localhost'];
  console.warn(`[config] Unrecognized hostname "${hostname}", falling back to beta`);
  return STAGE_MAP['beta.rocky.ai.aws.dev'];
}

export const appConfig = resolveConfig();

// ── Auth-code (BFF) flow feature flag ───────────────────────────────────────
//
// When true, login uses the Authorization-Code flow: response_type=code →
// the Gateway BFF (/auth/token) exchanges the code server-side and the
// refresh_token is locked in an HttpOnly cookie. When false, the legacy
// implicit flow (response_type='id_token token', tokens in the URL hash) is
// used. Staged rollout: all stages ON as of the prod cutover (gamma verified
// end-to-end, prod /auth/token confirmed reachable). Kept as a stage-derived
// constant (not inlined) so rollback = narrow this condition and redeploy.
export const AUTH_CODE_FLOW_ENABLED =
  appConfig.stage === 'dev' ||
  appConfig.stage === 'beta' ||
  appConfig.stage === 'gamma' ||
  appConfig.stage === 'prod';

// ── Office file upload (docx/xlsx) feature flag ─────────────────────────────
//
// When true, the chat upload accepts Office documents (.doc/.docx/.xls/.xlsx)
// in addition to the always-supported types (images, PDF, CSV, TXT, MD, etc.).
// These are read via Bedrock Converse native document blocks (file_read
// mode="document"), so no gateway change is required. Staged rollout: fully
// rolled out to ALL stages (dev/beta/gamma/prod) after beta validated
// real-world parsing quality. Kept as a stage-derived constant (not inlined)
// so rollback = narrow this condition and redeploy.
export const OFFICE_UPLOAD_ENABLED =
  appConfig.stage === 'dev' ||
  appConfig.stage === 'beta' ||
  appConfig.stage === 'gamma' ||
  appConfig.stage === 'prod';

// ── Portal zh/en UI switch feature flag ─────────────────────────────────────
//
// When true, the Portal UI starts in Chinese (until the user picks), the user
// popover offers a language switch, and <html lang> follows the choice. When
// false, the UI shows exactly the text it showed before the switch (the frozen
// `legacy` resources, see src/i18n), with no switch and no detection. Staged
// rollout: dev and beta first, then gamma and prod once beta was verified.
// Kept as a stage-derived constant (not inlined) so rollback = narrow this
// condition and redeploy.
export const UI_I18N_ENABLED =
  appConfig.stage === 'dev' ||
  appConfig.stage === 'beta' ||
  appConfig.stage === 'gamma' ||
  appConfig.stage === 'prod';

// ── Event Hub: sub-campaign attendees feature flag ──────────────────────────
//
// When true, the event detail page's Attendance List asks ListEventAttendees
// for `includeSubCampaigns`: the members of every campaign beneath the event
// in its campaign hierarchy, merged into one row per person (see
// eventHub/utils/attendeeMerger). When false, only the event's own campaign is
// listed. Needs AWSECEventHub CR-317896698 (deployed through prod); without
// it the service ignores the flag and returns no `memberships`. Rolled out to
// ALL stages. Kept as a stage-derived constant (not inlined) so rollback =
// narrow this condition and redeploy.
export const SUB_CAMPAIGN_ATTENDEES_ENABLED =
  appConfig.stage === 'dev' ||
  appConfig.stage === 'beta' ||
  appConfig.stage === 'gamma' ||
  appConfig.stage === 'prod';

// ── Federate helpers (exported for AuthContext) ─────────────────────────────

export const FEDERATE = {
  authEndpoint: `https://${appConfig.identityProvider}${FEDERATE_AUTH_PATH}`,
  idTokenKey: FEDERATE_ID_TOKEN_KEY,
  accessTokenKey: FEDERATE_ACCESS_TOKEN_KEY,
  /** Auth-code BFF endpoints on the Gateway (same-site with rocky.ai.aws.dev).
   *  All token minting goes through the Gateway; the SPA never calls Federate's
   *  token endpoint directly. tokenEndpoint mints id+access+refresh (code
   *  exchange); refreshEndpoint mints a new access_token only (refresh grant). */
  tokenEndpoint: `${appConfig.gatewayUrl}/auth/token`,
  refreshEndpoint: `${appConfig.gatewayUrl}/auth/refresh`,
  logoutEndpoint: `${appConfig.gatewayUrl}/auth/logout`,
} as const;

// ── Admin page access control ───────────────────────────────────────────────

// Gates /admin-rbms and the RBMS homepage editor, plus the "Backend Management
// System" entry in the user popover. The Gateway (homepage_config ADMIN_LDAP_GROUP)
// is the authoritative check for RBMS writes and must be kept in sync with this value.
export const ADMIN_PAGE_LDAP_GROUP = 'rocky-admin';
export const EVAL_PAGE_LDAP_GROUP = 'rocky-admin';

// ── Main app access ──────────────────────────────────────────────────────────
//
// Any of these three groups admits the main app (AccessGate). Which agents each
// user may chat with is AgentContext.isAgentAllowedFor:
//   rocky-admin                 every agent (ADMIN_PAGE_LDAP_GROUP, the same admin group)
//   rocky-user                  every agent except MARKETING_AGENT_IDS
//   rocky-marketing-agent-user  MARKETING_AGENT_IDS (added on top of rocky-user's set)
// All client-side UX gates: which agent a user may invoke is decided server-side by
// the Gateway's AgentAuthorizer (Permission Manager `invoke` grant), so the groups
// need matching grants there.
export const ROCKY_USER_LDAP_GROUP = 'rocky-user';
export const MARKETING_AGENT_LDAP_GROUP = 'rocky-marketing-agent-user';
export const MARKETING_AGENT_IDS: readonly string[] = ['marketing_agent'];

// Agents no one may chat with or pick, rocky-admin included. campaign_copilot_agent
// is being merged into marketing_agent (its skills already run there, DAT-9148), so it
// is hidden from the picker, the Account Settings selectors and ?agent=. Its runtime,
// AG-UI entry and homepage config are left in place: un-hiding = drop it from here
// and restore its AGENT_REGISTRY / SETTINGS_AGENTS entries
// (and MARKETING_AGENT_ORDER, WelcomeKey in AgentContext).
export const HIDDEN_AGENT_IDS: readonly string[] = ['campaign_copilot_agent'];
// ── Evaluation Agent chat on /eval ───────────────────────────────────────────
//
// When true, the Evaluation pages show a right-side ChatPanel pinned to the
// `eval` runtime agent (EVAL_CHAT_AGENT_ID). It is NOT a "Switch Agent" option.
// Staged rollout: all stages ON (beta verified end-to-end). Users also need the
// `eval` invoke grant (Permission Manager) on that stage. Rollback = narrow and redeploy.
export const EVAL_CHAT_ENABLED =
  appConfig.stage === 'dev' ||
  appConfig.stage === 'beta' ||
  appConfig.stage === 'gamma' ||
  appConfig.stage === 'prod';
// /admin/access-control has no client-side LDAP gate: authorization is per agent
// and server-side (Permission Manager `manage` grant or platform-admin Team).

// ── Multi-agent switch feature flag ──────────────────────────────────────────
//
// When true, the UserPopover shows "Switch Agent" radio buttons (Sales /
// Marketing) and the "Account & Setting" link. Now enabled in all stages,
// including gamma and prod: the backend session-filtering and agent-context
// plumbing (AgentContext, per-agent data scoping) are complete.
export const MULTI_AGENT_ENABLED = true;

// ── AG-UI chat panel: which agents, on which stage ───────────────────────────
//
// Agents listed for a stage render their chat via CopilotKit headless (AG-UI
// protocol); every other agent uses the legacy SSE ChatPanel. Rollout and
// rollback = edit this table and redeploy.
//
// ⚠️ Before adding an agent to a stage, the Gateway's AG-UI info `agents` map
// (CMHKAgentPlatformGateway channels/rocky/agui/relay.py) must already list it
// on that stage: CopilotKit's useAgent throws for an agent missing from it.
//
// marketing_agent (DAT-9170: it runs Campaign Copilot's skills, whose gates need
// AG-UI) went to dev / beta first, then gamma / prod once validated on beta.
type Stage = 'dev' | 'beta' | 'gamma' | 'prod';

const AGUI_AGENTS_BY_STAGE: Record<Stage, readonly string[]> = {
  dev: ['campaign_copilot_agent', 'marketing_agent'],
  beta: ['campaign_copilot_agent', 'marketing_agent'],
  gamma: ['campaign_copilot_agent', 'marketing_agent'],
  prod: ['campaign_copilot_agent', 'marketing_agent'],
};

/** Agents on the AG-UI panel for `stage`; empty for an unknown stage. */
export function aguiAgentsForStage(stage: string): ReadonlySet<string> {
  return new Set(Object.prototype.hasOwnProperty.call(AGUI_AGENTS_BY_STAGE, stage) ? AGUI_AGENTS_BY_STAGE[stage as Stage] : []);
}

export const AGUI_AGENTS = aguiAgentsForStage(appConfig.stage);

/** Whether any agent is on AG-UI on this stage (gates chatAgentFor's ?agent= override). */
export const AG_UI_ENABLED = AGUI_AGENTS.size > 0;

// ── Config-driven Workspace homepage feature flag ───────────────────────────
//
// When true, the user-facing homepage (WorkspaceHome) renders from the homepage
// config (static default today, DDB via Gateway later) instead of the original
// hard-coded layout. Now enabled on all stages including prod. Rollback = drop a
// stage from this condition (or set to false) and redeploy.
export const CONFIG_DRIVEN_HOMEPAGE_ENABLED =
  appConfig.stage === 'dev' ||
  appConfig.stage === 'beta' ||
  appConfig.stage === 'gamma' ||
  appConfig.stage === 'prod';
